import { factKindsFor, notACarrierName, queueWords, type TodoItem } from '@claims/core';
import { FACT_BUTTON, INLINE_KINDS } from './facts.js';
import { sortRows, writeSort, type ListSort, type ListSortSpec, type SortValue } from './list/list-sort.js';
import { PAGE_PARAM } from './list/list-page.js';
import { SF_COLUMN_WORDS } from './list/row-key.js';

/** THE QUEUE'S SORT (ported 2026-10-18 from the medrec console's sortable lists; DESIGN_SYSTEM 7a; Rod there: "i will need to be able to
 *  sort each column"). Every column but the key and the row's own panel (›) sorts, inside each group (the groups keep core todo.ts's order);
 *  the default is no column at all, the Queue's own ranking (urgent first in each group, core queue.ts), so "Clear the sort" gives it back.
 *  All unopened (a server page of 100 out of thousands) is sorted by the SERVER, the whole filtered set before the page (core unopened.ts,
 *  the Ins Creation chat 10-18), its Since being the case's age. The sort lives in the address
 *  (`?sort=&dir=`, list/list-sort.ts), one pair for the Queue's tabs. Pure, no React. */
export type QueueSortKey = 'since' | 'case' | 'record' | 'need' | 'action' | 'links';
export const QUEUE_SORT: ListSortSpec<QueueSortKey> = Object.freeze({
  keys: ['since', 'case', 'record', 'need', 'action', 'links'] as readonly QueueSortKey[],
  default: { key: null, dir: 'asc' } as ListSort<QueueSortKey>,
  words: {
    since: { name: 'Since', asc: 'oldest first', desc: 'newest first' },
    case: { name: 'Case', asc: 'A to Z', desc: 'Z to A' },
    record: { name: 'Record', asc: 'A to Z', desc: 'Z to A' },
    need: { name: 'What to do', asc: 'A to Z', desc: 'Z to A' },
    action: { name: 'Action', asc: 'A to Z', desc: 'Z to A' },
    // the sorted line names the column as its header reads (the medrec console's PR #482 header words)
    links: { name: SF_COLUMN_WORDS, asc: 'A to Z', desc: 'Z to A' },
  },
});

/** All unopened's words for the same columns: its Since is the case's age ("Age", oldest case first). The server sorts the whole list
 *  (core unopened.ts UNOPENED_SORT_KEYS = these keys), so its line never says "inside this page". */
export const UNOPENED_SORT: ListSortSpec<QueueSortKey> = Object.freeze({
  ...QUEUE_SORT,
  words: { ...QUEUE_SORT.words, since: { name: 'Age', asc: 'oldest case first', desc: 'newest case first' } },
});
/** The pager's order words: "oldest case first" (the default), "newest case first", else "by Record, A to Z". */
export function unopenedOrderWords(s: ListSort<QueueSortKey>): string {
  if (s.key === null) return UNOPENED_SORT.words.since.asc;
  const w = UNOPENED_SORT.words[s.key];
  return s.key === 'since' ? (s.dir === 'asc' ? w.asc : w.desc) : `by ${w.name}, ${s.dir === 'asc' ? w.asc : w.desc}`;
}
/** The address for a sort press: on All unopened the server's page 1 of the new order (the old page number would land mid-list); the
 *  Queue's tabs sort in hand, so the rest of the address stays as it is. */
export function unopenedSortParams(sp: URLSearchParams, next: ListSort<QueueSortKey>, tab: string): URLSearchParams {
  const out = writeSort(sp, next, QUEUE_SORT);
  if (tab === 'all') out.delete(PAGE_PARAM);
  return out;
}

const ms = (s: string | null | undefined): number | null => { const t = s ? Date.parse(s.replace(/\+0000$/, 'Z')) : NaN; return Number.isFinite(t) ? t : null; };

/** The words of the row's press, as the Action column draws it (queue.tsx's QueueAction): Read & reply, the typed fact's press, the main
 *  client's case, or the fix the row offers. So every "Enter policy #" sits together. */
export function actionWord(it: TodoItem, o: { lorSend: boolean; lorOnFile?: boolean; policeOrder?: boolean; writeBack: boolean }): string {
  if (it.group === 'reply') return 'Read & reply';
  const r = it.row;
  const kinds = factKindsFor(r, { lorSend: o.lorSend, lorOnFile: o.lorOnFile, policeOrder: o.policeOrder });
  if (kinds.length && INLINE_KINDS.has(kinds[0]!)) return FACT_BUTTON[kinds[0]!];
  if (r.secondClient && r.fix === 'companion_main' && r.status === 'need') return 'Main client\u{2019}s case';
  if (o.writeBack && kinds.length) return FACT_BUTTON[kinds[0]!];
  return r.status === 'need' || r.lorAsk ? r.fix ?? '' : '';
}

/** A row's value for a column. `since` is the start of the wait (All unopened passes the case's opening instead): ascending is oldest first. */
export function queueSortValue(it: TodoItem, key: QueueSortKey, o: { lorSend: boolean; lorOnFile?: boolean; policeOrder?: boolean; writeBack: boolean; sinceOf?: (it: TodoItem) => string | null }): SortValue {
  const r = it.row;
  switch (key) {
    case 'since': return ms(o.sinceOf ? o.sinceOf(it) : r.waitingSince);
    case 'case': return it.matter.client;
    case 'record': return r.carrier && !notACarrierName(r.carrier) && !r.gap ? r.carrier : null;
    case 'need': return queueWords(r.need ?? r.text);
    case 'action': return actionWord(it, o);
    case 'links': return it.matter.name;
  }
}

/** One group's (or one page's) items in the sort; no column keeps the order given. */
export function sortQueueItems(items: readonly TodoItem[], s: ListSort<QueueSortKey>, o: { lorSend: boolean; lorOnFile?: boolean; policeOrder?: boolean; writeBack: boolean; sinceOf?: (it: TodoItem) => string | null }): TodoItem[] {
  return sortRows(items, s, (it, k) => queueSortValue(it, k, o));
}
